import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HardHat, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../api/client';
import { saveSubcontractorSession } from '../api/subcontractorClient';
import { saveEmployerSession } from '../api/employer';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      // Dış kullanıcı (taşeron/işveren) kontrolü: backend ability'li token döner;
      // bu token normal 'token' anahtarına YAZILMAZ, ayrı session anahtarlarına gider.
      const res = await api.post('/api/v1/auth/login', { email, password });
      const data = res.data as {
        is_external?: boolean;
        external_type?: string;
        token?: string;
        user?: {
          id: number;
          name: string;
          surname: string;
          email: string;
          role: string;
          role_id: number | null;
          is_external: boolean;
          external_type: string;
        };
      };

      if (data?.is_external && data.token && data.user) {
        if (data.external_type === 'subcontractor') {
          saveSubcontractorSession(data.token, data.user, '');
          navigate('/subcontractor/tasks', { replace: true });
          return;
        }
        if (data.external_type === 'employer') {
          saveEmployerSession(data.token, data.user, '');
          navigate('/employer', { replace: true });
          return;
        }
      }

      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      let msg = 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';
      const axiosErr = err as {
        response?: { data?: { message?: string; error?: string }; status?: number };
        message?: string;
      };
      if (axiosErr.response?.data?.message) {
        msg = axiosErr.response.data.message;
      } else if (axiosErr.response?.data?.error) {
        msg = axiosErr.response.data.error;
      } else if (axiosErr.message) {
        msg = axiosErr.message;
      }
      // CSRF hatası özel mesaj
      if (axiosErr.response?.status === 419) {
        msg = 'Oturum süresi doldu. Lütfen sayfayı yenileyip tekrar deneyin.';
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-600 text-white">
            <HardHat className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Rizmir Şantiye Programı</h1>
          <p className="mt-1 text-sm text-slate-500">Hesabınıza giriş yapın</p>
        </div>

        {error && (
          <div data-testid="error-message" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
              E-posta
            </label>
            <input
              id="email"
              data-testid="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="ornek@rizmir.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
              Şifre
            </label>
            <input
              id="password"
              data-testid="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="••••••••"
            />
          </div>

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Şifremi unuttum
            </Link>
          </div>

          <button
            type="submit"
            data-testid="login-button"
            disabled={isLoading}
            className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Giriş yapılıyor...
              </>
            ) : (
              'Giriş Yap'
            )}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-400">
          Taşeron veya işveren daveti aldıysanız, davet kabulünde belirlediğiniz e-posta ve şifre ile giriş yapabilirsiniz.
        </p>
      </div>
    </div>
  );
}
